import { useState } from 'react';
import { ArrowRight, Briefcase, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Careers = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: 'general_application',
    experience: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/careers`, formData);
      toast.success(
        language === 'en'
          ? 'Application submitted successfully! We will contact you soon.'
          : 'Заявка успешно отправлена! Мы свяжемся с вами в ближайшее время.'
      );
      setFormData({
        name: '',
        email: '',
        phone: '',
        position: 'general_application',
        experience: '',
        message: '',
      });
    } catch (error) {
      toast.error(
        language === 'en'
          ? 'Failed to submit application. Please try again.'
          : 'Не удалось отправить заявку. Попробуйте еще раз.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <main data-testid="careers-page" className="min-h-screen pt-24">
      {/* Header with Video Background */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="https://images.pexels.com/videos/9654598/above-sea-boat-cargo-ship-india-9654598.jpeg?auto=compress&cs=tinysrgb&w=1920"
          className="absolute inset-0 w-full h-full object-cover z-0"
        >
          <source src="https://videos.pexels.com/video-files/9654598/9654598-uhd_2560_1440_24fps.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-navy/60 z-10" />
        
        <div className="container-bespoke relative z-20">
          <p className="text-editorial text-white/70 mb-6">
            {language === 'en' ? 'Join Our Team' : 'Присоединяйтесь к команде'}
          </p>
          <h1
            data-testid="careers-title"
            className="font-heading text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-8 drop-shadow-lg"
          >
            {language === 'en' ? 'Careers' : 'Карьера'}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            {language === 'en'
              ? 'Be part of a dynamic team shaping the future of maritime services in Turkey. We are always looking for talented individuals.'
              : 'Станьте частью динамичной команды, формирующей будущее морских услуг в Турции.'}
          </p>
        </div>
      </section>

      {/* No Open Positions Notice */}
      <section className="py-16 bg-slate-50">
        <div className="container-bespoke">
          <div className="bg-white border border-slate-200 p-8 flex items-start gap-6">
            <div className="bg-slate-100 p-4 rounded-full">
              <AlertCircle className="w-8 h-8 text-slate-400" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-navy mb-2">
                {language === 'en' ? 'No Open Positions Currently' : 'В настоящее время вакансий нет'}
              </h2>
              <p className="text-slate-600">
                {language === 'en'
                  ? 'There are no open positions at the moment. However, we always welcome talented individuals. Feel free to submit a general application and we will keep your information on file for future opportunities.'
                  : 'В настоящее время открытых вакансий нет. Тем не менее, мы всегда рады талантливым людям. Вы можете отправить общую заявку, и мы сохраним вашу информацию для будущих возможностей.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container-bespoke">
          <div className="grid lg:grid-cols-12 gap-16">
            {/* Form */}
            <div className="lg:col-span-7">
              <p className="text-editorial text-slate-500 mb-4">
                {language === 'en' ? 'General Application' : 'Общая заявка'}
              </p>
              <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-navy mb-8">
                {language === 'en' ? 'Submit Your Application' : 'Отправить заявку'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-editorial text-navy">
                      {language === 'en' ? 'Full Name' : 'Полное имя'} *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="border-slate-200 focus:border-signal"
                      placeholder={language === 'en' ? 'Your full name' : 'Ваше полное имя'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-editorial text-navy">
                      {language === 'en' ? 'Email' : 'Эл. почта'} *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="border-slate-200 focus:border-signal"
                      placeholder={language === 'en' ? 'your@email.com' : 'ваш@email.com'}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-editorial text-navy">
                      {language === 'en' ? 'Phone Number' : 'Телефон'} *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="border-slate-200 focus:border-signal"
                      placeholder="+90 5XX XXX XX XX"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="experience" className="text-editorial text-navy">
                      {language === 'en' ? 'Years of Experience' : 'Опыт работы'} *
                    </Label>
                    <Input
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      required
                      className="border-slate-200 focus:border-signal"
                      placeholder={language === 'en' ? 'e.g., 5 years in maritime' : 'например, 5 лет в морской отрасли'}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-editorial text-navy">
                    {language === 'en' ? 'Cover Letter / About You' : 'Сопроводительное письмо'} *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="border-slate-200 focus:border-signal resize-none"
                    placeholder={
                      language === 'en'
                        ? 'Tell us about yourself, your experience, skills, and why you want to join Rota Maritime Agency...'
                        : 'Расскажите о себе, своем опыте, навыках и почему хотите присоединиться к Rota Maritime Agency...'
                    }
                  />
                </div>

                <button
                  type="submit"
                  data-testid="career-submit"
                  disabled={loading}
                  className="btn-signal w-full md:w-auto disabled:opacity-50"
                >
                  {loading
                    ? (language === 'en' ? 'Submitting...' : 'Отправка...')
                    : (language === 'en' ? 'Submit Application' : 'Отправить заявку')}
                  <ArrowRight className="inline ml-2 w-4 h-4" />
                </button>
              </form>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-5">
              <div className="bg-navy p-8 lg:p-12 text-white">
                <h3 className="font-heading text-2xl font-bold mb-6">
                  {language === 'en' ? 'Why Join Us?' : 'Почему мы?'}
                </h3>
                
                <div className="space-y-6">
                  <div className="border-b border-white/10 pb-6">
                    <p className="text-signal font-bold mb-2">
                      {language === 'en' ? '35+ Years of Excellence' : '35+ лет совершенства'}
                    </p>
                    <p className="text-slate-400 text-sm">
                      {language === 'en'
                        ? 'Join a company with a proven track record in the maritime industry.'
                        : 'Присоединяйтесь к компании с проверенной репутацией в морской отрасли.'}
                    </p>
                  </div>
                  
                  <div className="border-b border-white/10 pb-6">
                    <p className="text-signal font-bold mb-2">
                      {language === 'en' ? 'Career Growth' : 'Карьерный рост'}
                    </p>
                    <p className="text-slate-400 text-sm">
                      {language === 'en'
                        ? 'We invest in our employees and provide opportunities for advancement.'
                        : 'Мы инвестируем в сотрудников и предоставляем возможности для продвижения.'}
                    </p>
                  </div>
                  
                  <div className="border-b border-white/10 pb-6">
                    <p className="text-signal font-bold mb-2">
                      {language === 'en' ? 'Dynamic Environment' : 'Динамичная среда'}
                    </p>
                    <p className="text-slate-400 text-sm">
                      {language === 'en'
                        ? 'Work with international clients and diverse maritime operations.'
                        : 'Работайте с международными клиентами и разнообразными морскими операциями.'}
                    </p>
                  </div>

                  <div>
                    <p className="text-signal font-bold mb-2">
                      {language === 'en' ? 'Contact HR' : 'Связаться с HR'}
                    </p>
                    <a href="mailto:hr@rotaship.com" className="text-white hover:text-signal transition-colors">
                      hr@rotaship.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
